import { Box, styled } from "@mui/material";
import { PragungParametersLayout } from "../styles";
import { useMemo } from "react";
import KeyCard from "./KeyCard/KeyCard";
import { SCROLL_CSS } from "../../../../constants/common";
import {
  IKeySideFont,
  IKeySideLogoSize,
  IKeySideName,
  IKeyType,
  IKeysModalsCommon,
} from "../../../../store/keysTypes";
import { useTranslation } from "react-i18next";
import { IKeysBlankNames } from "../../../../constants/pragerohlinge";
import { useKeyCustomization } from "../../../../hooks/useKeyCustomization";
import { useKeysModal } from "../../../../hooks/useKeysModal";

const Wrapper = styled(Box)`
  padding-top 5px;
  width: 100%;
  ${SCROLL_CSS};
`;

const PragungParameters = () => {
  const { currentSelectedKey, selectedKeyTypes, updateKeyProperty } =
    useKeyCustomization();
  const { openModal } = useKeysModal();
  const { t } = useTranslation();

  const selectedKeyIndex = useMemo(() => {
    if (currentSelectedKey?.blank) {
      return selectedKeyTypes[currentSelectedKey.blank].findIndex(
        (key) => key.id === currentSelectedKey.id
      );
    }
    return 0;
  }, [selectedKeyTypes, currentSelectedKey]);

  const handleChangeType = (
    value: IKeyType,
    side: IKeySideName,
    blank: IKeysBlankNames
  ) => {
    if (blank === IKeysBlankNames.FA || blank === IKeysBlankNames.OV) {
      selectedKeyTypes[blank].forEach((item, keyIndex) => {
        updateKeyProperty({ keyIndex, blank, side, property: "type", value });
      });
      return;
    }
    updateKeyProperty({
      keyIndex: selectedKeyIndex,
      blank,
      side,
      property: "type",
      value,
    });
  };

  const handleChangeTextField = (
    value: string,
    index: number,
    side: IKeySideName,
    blank: IKeysBlankNames
  ) => {
    if (blank === IKeysBlankNames.FA || blank === IKeysBlankNames.OV) {
      selectedKeyTypes[blank].forEach((item, keyIndex) => {
        updateKeyProperty({
          keyIndex,
          blank,
          side,
          property: "text",
          value,
          textIndex: index,
        });
      });
      return;
    }
    updateKeyProperty({
      keyIndex: selectedKeyIndex,
      blank,
      side,
      property: "text",
      value,
      textIndex: index,
    });
  };

  const handleChangeFont = (
    value: IKeySideFont,
    side: IKeySideName,
    blank: IKeysBlankNames
  ) => {
    if (blank === IKeysBlankNames.FA || blank === IKeysBlankNames.OV) {
      selectedKeyTypes[blank].forEach((item, keyIndex) => {
        updateKeyProperty({ keyIndex, blank, side, property: "font", value });
      });
      return;
    }
    updateKeyProperty({
      keyIndex: selectedKeyIndex,
      blank,
      side,
      property: "font",
      value,
    });
  };

  const handleChangeSize = (
    name: string,
    value: number,
    side: IKeySideName,
    blank: IKeysBlankNames
  ) => {
    if (blank === IKeysBlankNames.FA || blank === IKeysBlankNames.OV) {
      selectedKeyTypes[blank].forEach((item, keyIndex) => {
        updateKeyProperty({
          keyIndex,
          blank,
          side,
          property: "fontSize",
          value: { name, size: value },
        });
      });
      return;
    }
    updateKeyProperty({
      keyIndex: selectedKeyIndex,
      blank,
      side,
      property: "fontSize",
      value: { name, size: value },
    });
  };

  const handleChangeLogo = (
    value: string,
    side: IKeySideName,
    blank: IKeysBlankNames
  ) => {
    if (blank === IKeysBlankNames.FA || blank === IKeysBlankNames.OV) {
      selectedKeyTypes[blank].forEach((item, keyIndex) => {
        updateKeyProperty({ keyIndex, blank, side, property: "logo", value });
      });
      return;
    }
    updateKeyProperty({
      keyIndex: selectedKeyIndex,
      blank,
      side,
      property: "logo",
      value,
    });
  };

  const handleChangeLogoSize = (
    value: IKeySideLogoSize,
    side: IKeySideName,
    blank: IKeysBlankNames
  ) => {
    if (blank === IKeysBlankNames.FA || blank === IKeysBlankNames.OV) {
      selectedKeyTypes[blank].forEach((item, keyIndex) => {
        updateKeyProperty({
          keyIndex,
          blank,
          side,
          property: "logoSize",
          value,
        });
      });
      return;
    }
    updateKeyProperty({
      keyIndex: selectedKeyIndex,
      blank,
      side,
      property: "logoSize",
      value,
    });
  };

  const handleScreenshot = (
    value: string,
    side: IKeySideName,
    blank: IKeysBlankNames
  ) => {
    if (blank === IKeysBlankNames.FA || blank === IKeysBlankNames.OV) {
      selectedKeyTypes[blank].forEach((item, keyIndex) => {
        updateKeyProperty({ keyIndex, blank, side, property: "image", value });
      });
      return;
    }
    updateKeyProperty({
      keyIndex: selectedKeyIndex,
      blank,
      side,
      property: "image",
      value,
    });
  };

  const handleOpenModal = (value: IKeysModalsCommon) => {
    openModal(value);
  };

  return (
    <Wrapper>
      {currentSelectedKey && (
        <PragungParametersLayout>
          <KeyCard
            selectedKey={currentSelectedKey}
            title={t("embossing.front-side")}
            text={currentSelectedKey.front.text}
            type={currentSelectedKey.front.type}
            font={currentSelectedKey.front.font}
            size={currentSelectedKey.front.size}
            logo={currentSelectedKey.front.logo}
            blank={currentSelectedKey.blank}
            profile={currentSelectedKey.value}
            logoSize={currentSelectedKey.front.logoSize}
            side={IKeySideName.FRONT}
            onChangeType={(value) =>
              handleChangeType(
                value as IKeyType,
                IKeySideName.FRONT,
                currentSelectedKey.blank
              )
            }
            onChangeTextField={(value, index) =>
              handleChangeTextField(
                value,
                index,
                IKeySideName.FRONT,
                currentSelectedKey.blank
              )
            }
            onChangeFont={(value) =>
              handleChangeFont(
                value as IKeySideFont,
                IKeySideName.FRONT,
                currentSelectedKey.blank
              )
            }
            onChangeSize={(name, value) =>
              handleChangeSize(
                name,
                value,
                IKeySideName.FRONT,
                currentSelectedKey.blank
              )
            }
            onChangeLogo={(value) =>
              handleChangeLogo(
                value,
                IKeySideName.FRONT,
                currentSelectedKey.blank
              )
            }
            onChangeLogoSize={(value) =>
              handleChangeLogoSize(
                value,
                IKeySideName.FRONT,
                currentSelectedKey.blank
              )
            }
            onOpenModal={(value) => handleOpenModal(value)}
            onScreenshot={(value) =>
              handleScreenshot(
                value,
                IKeySideName.FRONT,
                currentSelectedKey.blank
              )
            }
          />

          <KeyCard
            selectedKey={currentSelectedKey}
            title={t("embossing.back-side")}
            text={currentSelectedKey.back.text}
            type={currentSelectedKey.back.type}
            font={currentSelectedKey.back.font}
            size={currentSelectedKey.back.size}
            logo={currentSelectedKey.back.logo}
            blank={currentSelectedKey.blank}
            profile={currentSelectedKey.value}
            logoSize={currentSelectedKey.back.logoSize}
            side={IKeySideName.BACK}
            onChangeType={(value) =>
              handleChangeType(
                value as IKeyType,
                IKeySideName.BACK,
                currentSelectedKey.blank
              )
            }
            onChangeTextField={(value, index) =>
              handleChangeTextField(
                value,
                index,
                IKeySideName.BACK,
                currentSelectedKey.blank
              )
            }
            onChangeFont={(value) =>
              handleChangeFont(
                value as IKeySideFont,
                IKeySideName.BACK,
                currentSelectedKey.blank
              )
            }
            onChangeSize={(name, value) =>
              handleChangeSize(
                name,
                value,
                IKeySideName.BACK,
                currentSelectedKey.blank
              )
            }
            onChangeLogo={(value) =>
              handleChangeLogo(
                value,
                IKeySideName.BACK,
                currentSelectedKey.blank
              )
            }
            onChangeLogoSize={(value) =>
              handleChangeLogoSize(
                value,
                IKeySideName.BACK,
                currentSelectedKey.blank
              )
            }
            onOpenModal={handleOpenModal}
            onScreenshot={(value) =>
              handleScreenshot(
                value,
                IKeySideName.BACK,
                currentSelectedKey.blank
              )
            }
          />
        </PragungParametersLayout>
      )}
    </Wrapper>
  );
};

export default PragungParameters;
